import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { StatusPill, fmtRelative } from "@/lib/workflow";
import { Inbox } from "lucide-react";

const TABS = [
  {
    id: "pending",
    label: "Pending",
    matches: ["pending_vendor_acceptance"],
    color: "amber",
  },
  {
    id: "active",
    label: "Active",
    matches: [
      "vendor_accepted",
      "consumer_engaged",
      "intake_completed",
      "fulfillment_in_progress",
    ],
    color: "blue",
  },
  {
    id: "completed",
    label: "Completed",
    matches: ["completed", "invoice_submitted", "payment_pending", "paid"],
    color: "emerald",
  },
  {
    id: "cancelled",
    label: "Cancelled",
    matches: ["cancelled", "disputed"],
    color: "rose",
  },
];

const TAB_BG = {
  amber: "bg-amber-100 text-amber-800",
  blue: "bg-blue-100 text-blue-800",
  emerald: "bg-emerald-100 text-emerald-800",
  rose: "bg-rose-100 text-rose-700",
};

export default function VendorOrders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  useEffect(() => {
    api
      .get("/workflow/requests")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => {
      c[t.id] = items.filter((it) => t.matches.includes(it.status)).length;
    });
    return c;
  }, [items]);

  const visible = useMemo(() => {
    const t = TABS.find((x) => x.id === tab);
    return items.filter((it) => t.matches.includes(it.status));
  }, [items, tab]);

  return (
    <div className="space-y-6" data-testid="vendor-orders-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700">
          Vendor · Order Management
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1">Inbound clinic orders</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Triage every order from request → acceptance → fulfilment → invoice. Tabs reflect the
          shape of your work in real time.
        </p>
      </div>

      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="vendor-orders-tabs"
        role="tablist"
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`vendor-orders-tab-${t.id}`}
            role="tab"
            aria-selected={tab === t.id}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold transition ${
              tab === t.id
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            <span>{t.label}</span>
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                tab === t.id ? "bg-white/20 text-white" : TAB_BG[t.color]
              }`}
            >
              {counts[t.id] || 0}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : visible.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center"
          data-testid="vendor-orders-empty"
        >
          <Inbox className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-600">No {tab} orders right now.</div>
        </div>
      ) : (
        <div
          className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
          data-testid="vendor-orders-table"
        >
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Order</th>
                <th className="text-left px-4 py-3 font-semibold">Workflow</th>
                <th className="text-left px-4 py-3 font-semibold">Clinic</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((it) => (
                <tr
                  key={it.id}
                  data-testid={`vendor-order-row-${it.id}`}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => (window.location.href = `/vendor/workflows/${it.id}`)}
                >
                  <td className="px-4 py-3 font-mono text-xs text-slate-800">
                    <Link
                      to={`/vendor/workflows/${it.id}`}
                      className="hover:underline text-emerald-700 font-semibold"
                    >
                      {it.order_id}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-700 max-w-[24ch] truncate">
                    {it.requested_workflow}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{it.clinic_name || "—"}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={it.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-500">{fmtRelative(it.updated_at || it.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
